// Applies the teacher's site theme (branding.themeDefinition, see docs/themes/THEME-SPEC.md) to the page:
// colour/shape/font tokens as CSS variables on <html>, the layout block names as data-* attributes (styles/themes.scss
// turns them into the actual look), the reading direction, the UI language and the Google Fonts.
//
// Only three colours come from the theme or the teacher (primary, accent, background); every other colour is
// derived here so text always stays readable -- the same maths as the theme spec page and ThemeDefinition/Colors.java.

import { setLanguage } from "./i18n";

const FONT_WEIGHTS = {
  Cairo: "400;600;700;800", Tajawal: "400;500;700;800", Almarai: "400;700;800", "IBM Plex Sans Arabic": "400;500;600;700",
  "Readex Pro": "400;500;600;700", "Noto Kufi Arabic": "400;600;700;800", Changa: "400;600;700;800", "El Messiri": "400;600;700",
  Amiri: "400;700", "Reem Kufi": "400;600;700", "Baloo Bhaijaan 2": "400;600;700;800", Lalezar: null,
  Inter: "400;500;600;700;800", Poppins: "400;500;600;700;800", "Baloo 2": "400;600;700;800", "DM Sans": "400;500;700",
  "Playfair Display": "400;700;800",
};

const SHADOWS = {
  none: ["none", "none"],
  soft: ["0 2px 8px rgba(15,23,42,.06)", "0 14px 34px -12px rgba(15,23,42,.18)"],
  strong: ["0 6px 16px -8px rgba(0,0,0,.18)", "0 22px 44px -14px rgba(0,0,0,.30)"],
};

// ---- colour maths ----
function rgb(hex) {
  let h = String(hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function toHex(c) {
  return "#" + c.map((v) => ("0" + Math.round(v).toString(16)).slice(-2)).join("");
}
export function mix(a, b, t) {
  const x = rgb(a), y = rgb(b);
  return toHex(x.map((v, i) => v + (y[i] - v) * t));
}
export function luminance(hex) {
  return rgb(hex)
    .map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); })
    .reduce((s, v, i) => s + v * [0.2126, 0.7152, 0.0722][i], 0);
}
export function contrast(a, b) {
  const x = luminance(a), y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
function onColor(c) {
  return contrast(c, "#ffffff") >= contrast(c, "#111111") ? "#ffffff" : "#111111";
}
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Every token from the three base colours. */
export function deriveColors(primary, accent, bg) {
  const dark = luminance(bg) < 0.18;
  let ink = dark ? "#eef0f5" : mix(primary, "#0b0d12", 0.82);
  if (contrast(ink, bg) < 7) ink = dark ? "#ffffff" : "#0b0d12";
  return {
    dark,
    "--t-primary": primary,
    "--t-accent": accent,
    "--t-bg": bg,
    "--t-ink": ink,
    "--t-surface": dark ? mix(bg, "#ffffff", 0.06) : mix(bg, "#ffffff", 0.75),
    "--t-bg-soft": dark ? mix(bg, "#ffffff", 0.03) : mix(bg, primary, 0.05),
    "--t-muted": mix(ink, bg, 0.42),
    "--t-line": mix(ink, bg, dark ? 0.82 : 0.86),
    "--t-on-primary": onColor(primary),
    "--t-on-accent": onColor(accent),
    "--t-primary-soft": mix(primary, bg, dark ? 0.8 : 0.88),
    "--t-accent-soft": mix(accent, bg, dark ? 0.8 : 0.82),
  };
}

function parse(definition) {
  if (!definition) return null;
  if (typeof definition === "object") return definition;
  try {
    return JSON.parse(definition);
  } catch (e) {
    return null;
  }
}

function loadFont(name) {
  if (!name || !(name in FONT_WEIGHTS)) return;
  const id = "font-" + name.replace(/\s+/g, "-");
  if (document.getElementById(id)) return;
  const w = FONT_WEIGHTS[name];
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=" + name.replace(/ /g, "+") + (w ? ":wght@" + w : "") + "&display=swap";
  document.head.appendChild(link);
}

/** Applies a theme file + the teacher's choices ({primaryColor, accentColor, bgColor, fontBody, fontHeading}). */
export function applyTheme(definition, choice) {
  const def = parse(definition);
  const root = document.documentElement;
  if (!def) {
    setLanguage("ar");
    return;
  }
  const c = choice || {};
  const preset = (def.presets && def.presets[0]) || {};
  const pick = (v, d) => (v && HEX.test(v) ? v : d);
  const colors = deriveColors(pick(c.primaryColor, preset.primary), pick(c.accentColor, preset.accent), pick(c.bgColor, preset.background));
  const shape = def.shape || {};
  const shadow = SHADOWS[shape.shadow] || SHADOWS.soft;
  const body = (c.fontBody in FONT_WEIGHTS && c.fontBody) || (def.fonts && def.fonts.body) || "Cairo";
  const head = (c.fontHeading in FONT_WEIGHTS && c.fontHeading) || (def.fonts && def.fonts.heading) || body;

  const vars = Object.assign({}, colors, {
    "--t-radius": (shape.radius != null ? shape.radius : 14) + "px",
    "--t-radius-lg": (shape.radiusLarge != null ? shape.radiusLarge : 22) + "px",
    "--t-radius-btn": (shape.buttonRadius != null ? shape.buttonRadius : 10) + "px",
    "--t-border": (shape.borderWidth != null ? shape.borderWidth : 1) + "px",
    "--t-shadow-sm": shadow[0],
    "--t-shadow": shadow[1],
    "--t-font-body": `'${body}'`,
    "--t-font-head": `'${head}'`,
  });
  delete vars.dark;
  Object.keys(vars).forEach((k) => root.style.setProperty(k, vars[k]));

  const layout = def.layout || {};
  root.setAttribute("dir", def.direction === "ltr" ? "ltr" : "rtl");
  root.setAttribute("lang", def.language === "en" ? "en" : "ar");
  root.dataset.theme = def.id || "";
  root.dataset.header = layout.header || "bar";
  root.dataset.hero = layout.hero || "split";
  root.dataset.cards = layout.courseCards || "lift";
  root.dataset.titles = layout.sectionTitles || "plain";
  root.dataset.footer = layout.footer || "simple";
  root.dataset.shadow = shape.shadow || "soft";
  // the newer themes' design language, hero drawing and specialty section ("none" when a theme predates them)
  root.dataset.style = layout.style || "none";
  root.dataset.art = layout.art || "none";
  root.dataset.subject = layout.subject || "none";
  root.dataset.mode = colors.dark ? "dark" : "light";
  setLanguage(def.language);
  loadFont(body);
  loadFont(head);

  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", colors["--t-bg"]);
}

/** "?themePreview=<id>&c=<primary>,<accent>,<bg>&fb=<font>&fh=<font>" -- the teacher's dashboard opens the site like
 *  this to show a theme before choosing it. Display only; nothing is saved. Returns null when not previewing. */
const PREVIEW_KEY = "theme-preview";

export function previewRequest() {
  let q;
  try {
    q = new URLSearchParams(window.location.search);
    if (!q.get("themePreview")) {
      // keep previewing while the teacher clicks around their site in this tab
      const saved = window.sessionStorage.getItem(PREVIEW_KEY);
      if (saved) q = new URLSearchParams(saved);
    } else {
      window.sessionStorage.setItem(PREVIEW_KEY, q.toString());
    }
  } catch (e) {
    if (!q) return null;
  }
  const id = q.get("themePreview");
  if (!id || !/^[a-z][a-z0-9-]{1,39}$/.test(id)) return null;
  const colors = (q.get("c") || "").split(",");
  const ok = (v) => (v && HEX.test(v) ? v : null);
  return {
    id,
    choice: {
      primaryColor: ok(colors[0]),
      accentColor: ok(colors[1]),
      bgColor: ok(colors[2]),
      fontBody: q.get("fb") || null,
      fontHeading: q.get("fh") || null,
    },
  };
}

/** True when the site is drawn inside another page -- the teacher's dashboard showing it as a live preview. */
export function isEmbedded() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // a cross-origin parent refuses even the comparison
  }
}

/** While previewing inside the dashboard, the teacher's unsaved colours and fonts arrive as messages
 *  ({ type: "manasa:theme-draft", choice }) and are re-applied at once -- no reload, the page keeps its place.
 *  Only the parent frame is listened to, and applyTheme accepts nothing but #RRGGBB colours and known fonts. */
let draftListener = null;

export function listenForDraft(definition) {
  if (!isEmbedded() || draftListener) return;
  draftListener = (event) => {
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!data || data.type !== "manasa:theme-draft" || typeof data.choice !== "object") return;
    applyTheme(definition, data.choice);
  };
  window.addEventListener("message", draftListener);
  // tell the dashboard it can start sending (it may have drafted before the page finished loading)
  window.parent.postMessage({ type: "manasa:theme-ready" }, "*");
}

export function endPreview() {
  try {
    window.sessionStorage.removeItem(PREVIEW_KEY);
  } catch (e) {
    /* storage blocked */
  }
  window.location.href = window.location.pathname;
}
